import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { AccountInfo } from '@azure/msal-browser'
import { apiJson, api, ApiError } from '../api'
import { LeaderboardPage } from './Leaderboard'
import { GuidesPage } from './Guides'
import { findGuide } from '../guides/registry'
import { GUIDE_ARTICLE_CSS } from '../guides/articleStyles'
import { OperatorAvatar } from '../components/AppShell'
import peekMascot from '../assets/chu-peek.png'
// Chubacca: thirteen transparent clips on a random rotation, sound on
// click (Maroun, 24 Sep 2026). See components/ChubaccaMascot.tsx.
import { ChubaccaMascot } from '../components/ChubaccaMascot'
import { arcadeCss } from '../arcade/arcadeCss'
import { TopicShareBar, ArcadeHeader } from '../arcade/atoms'
import { toViewModel } from '../arcade/viewModel'
import type { TopicCount, LeaderboardPayload, BoardModel, BoardPlayer } from '../arcade/viewModel'
import { resolveArcadeTheme } from '../arcade/themes'
import type { ArcadeTheme } from '../arcade/themes'
import { LineChart, Donut, RankedBars, Heatmap, ChartLegend, fmtInt } from '../arcade/charts'
import type { LineSeries, BarRow, HeatCell, Fmt } from '../arcade/charts'
import { BentoBoardRow } from '../arcade/skeletons'

/**
 * The staff portal shell: Home, Leaderboard, Developer key -- three tabs on
 * the arcade host (arcade.chugpt.apps.chu.com.au), behind `requireUser`
 * only.
 *
 * RESTYLED 23 Sep 2026 onto the arcade's OWN theme system, after the first
 * version (hand-rolled dark styles) read as a different product from the
 * leaderboard it sits beside. This wraps every screen in `className="arcade"`
 * and injects `arcadeCss(theme)`, the SAME mechanism every arcade skeleton
 * paints through (`--a-*` custom properties, see `arcade/arcadeCss.ts`'s own
 * doc comment) -- Home and Developer key are therefore Bento HQ tiles by
 * construction, not an approximation of them.
 *
 * THEME IS `resolveArcadeTheme('bento')`, NOT `useArcadeTheme`, as of round
 * 13. The cabinet picker (and the per-viewer localStorage choice it wrote)
 * is gone from both arcade mounts -- Bento HQ is the only theme reachable
 * anywhere in the product now, on the Leaderboard tab (`pages/Leaderboard.tsx`)
 * and here. Calling the old hook here after that change shipped would have
 * left exactly the inconsistency it was meant to remove: anyone with a
 * leftover stored choice from before the picker existed would see a
 * DIFFERENT theme on Home/sign-in than on the Leaderboard tab, since only
 * `Leaderboard.tsx` would have stopped reading it. Caught in the round-13
 * design review, not shipped separately from it.
 *
 * This still wraps the EXISTING `LeaderboardPage` unchanged for its own tab
 * rather than folding Home/Developer-key into that component's `Mode`
 * union: that union drives one big server-computed leaderboard payload with
 * a section per mode, and Home/Developer-key read entirely different
 * endpoints that have nothing to do with it.
 */

type PortalTab = 'home' | 'leaderboard' | 'devkey' | 'guides' | 'budget'

/** Reads the tab from the URL path, the same convention
 *  `AppShell.tsx`'s `isArcade` check and `Leaderboard.tsx`'s `/arcade/u/<id>`
 *  deep link already use (a plain path match, no react-router `<Route>` for
 *  this host). The onboarding email links straight to `/developer-key`, so
 *  this is not cosmetic: opening that link must land on that tab, not Home.
 *  `/guides/<slug>` is a PREFIX match (`startsWith`, not `endsWith`) since a
 *  guide slug follows it -- see `pages/Guides.tsx`'s own path helpers, which
 *  own everything after `/guides`. */
function tabFromPath(pathname: string): PortalTab {
  if (pathname.endsWith('/developer-key')) return 'devkey'
  if (pathname.endsWith('/leaderboard')) return 'leaderboard'
  if (pathname.endsWith('/budget')) return 'budget'
  if (pathname.includes('/guides')) return 'guides'
  return 'home'
}

function pathForTab(tab: PortalTab): string {
  if (tab === 'devkey') return '/developer-key'
  if (tab === 'budget') return '/budget'
  if (tab === 'leaderboard') return '/leaderboard'
  if (tab === 'guides') return '/guides'
  return '/'
}

interface ChatPosition {
  spend: number | null
  cap: number | null
  unlimited: boolean
}

type DevKeyStatus =
  | { status: 'not_eligible' }
  | { status: 'eligible' }
  | { status: 'active'; alias: string; spend: number | null; budget: number | null; blocked: boolean }

interface MeSummary {
  email: string
  hideCosts: boolean
  chat: ChatPosition
  devKey: DevKeyStatus
}

interface McpServerUsage {
  id: string
  displayName: string
  calls: number
  conversations: number
}

interface DepartmentRank {
  department: string | null
  departmentRank: number | null
  departmentTotal: number
  orgRank: number | null
  orgTotal: number
}

interface UsageDayPoint {
  day: string
  chatSpend: number | null
  codeSpend: number | null
  chatTokens: number
  codeTokens: number
  chatRequests: number
  codeRequests: number
}

interface ModelUsageRow {
  model: string
  spend: number | null
  tokens: number
  requests: number
}

interface HourCell {
  dow: string
  hour: number
  requests: number
}

interface UsageSummary {
  splitAvailable: boolean
  daily: UsageDayPoint[]
  totals: {
    chatSpend: number | null
    codeSpend: number | null
    chatTokens: number
    codeTokens: number
    chatRequests: number
    codeRequests: number
  }
  models: ModelUsageRow[]
  hourly: HourCell[]
  teamHeatmap: ComparisonHeatmap
  companyHeatmap: ComparisonHeatmap
  desktop: { measured: false; reason: string }
}

interface ComparisonHeatmap {
  cells: Array<{ dow: string; hour: number; value: number }>
  headcount: number
  unit: 'requests per person'
}

/** The window the backend actually resolved (round 12) -- echoed back so
 *  the frontend never re-derives what it asked for. `boardWindowKey` is
 *  what `loadBoard` passes to `/api/leaderboard?window=`, reusing that
 *  endpoint's own vocabulary rather than guessing a second one. */
interface ResolvedActivityWindow {
  key: '1h' | 'day' | 'wk' | 'mo' | 'custom'
  boardWindowKey: '1h' | 'day' | 'wk' | 'mo' | null
  bucketUnit: 'minute' | 'hour' | 'day'
  subDay: boolean
}

interface MeActivity {
  window: ResolvedActivityWindow
  taxonomy: { month: TopicCount[]; allTime: TopicCount[]; available: boolean }
  mcp: { servers: McpServerUsage[]; topTools: Array<{ id: string; displayName: string; tool: string; calls: number }>; available: boolean }
  rank: DepartmentRank
  usage: UsageSummary
}

interface DevKeyUsageDay {
  day: string
  spend: number | null
  tokens: number
  requests: number
}

type DevKeyDetail =
  | { status: 'not_eligible' }
  | { status: 'no_key' }
  | {
      status: 'active'
      alias: string | null
      blocked: boolean
      createdAt: string | null
      budgetDuration: string | null
      budgetResetAt: string | null
      expires: string | null
      models: string[]
      spend: number | null
      budget: number | null
      usage: { daily: DevKeyUsageDay[]; lastUsed: string | null; requestsThisCycle: number; tokensThisCycle: number }
    }

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (HTTP ${err.status})`
  return err instanceof Error ? err.message : String(err)
}

// Model DISPLAY LABELS ("Sonnet 5") are computed once, server-side, in
// `clients/modelNames.ts` -- both `usage.models` (Home's ranked bars) and
// the developer key detail's `models` list arrive already normalised and
// merged, so there is no second label map here to drift out of sync with
// the backend's (this page used to carry one; removed 23 Sep 2026 round 10).

function fmtMoney(n: number): string {
  return `$${n.toFixed(2)}`
}

/** The developer key's cycle spend/budget line, "Hidden" when hideCosts has
 *  nulled it -- the same rule `chatCycleLine` below already follows, so a
 *  hidden dollar figure never reads differently in two places on the same
 *  page. */
function devKeyCycleLine(spend: number | null, budget: number | null): string {
  if (spend === null) return 'Hidden'
  return `${fmtMoney(spend)} / ${budget === null ? 'unlimited' : fmtMoney(budget)}`
}

/** The chat ceiling/spend line, shared by Home's "This month's chat usage"
 *  tile and the Budget tab's own (Round 16, Unit 18) -- one definition, so
 *  the two pages can never disagree on what "Hidden" or "unlimited" means
 *  for the same figure. */
function chatCycleLine(chat: ChatPosition): string {
  if (chat.spend === null) return 'Hidden'
  return `${fmtMoney(chat.spend)}${chat.unlimited ? '' : ` / $${(chat.cap ?? 0).toFixed(0)}`}`
}

function fmtDate(iso: string | null): string {
  if (!iso) return 'Unknown'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'Unknown'
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** 4-hour bucket start (0, 4, 8...) -> the label the heatmap prints, matching
 *  the buckets `routes/me.ts`'s `HOUR_BUCKET_HOURS` groups by. */
function hourBucketLabel(hour: number): string {
  const end = (hour + 4) % 24
  return `${String(hour).padStart(2, '0')}-${String(end).padStart(2, '0')}`
}
const HOUR_COLS = [0, 4, 8, 12, 16, 20].map(hourBucketLabel)
const WEEKDAY_ROWS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Team/company cells are "requests per person" and can be fractional
 *  (a 5-person team averaging 2.4 requests in a slot is a real number, not
 *  an error) -- `fmtInt` would round it to a misleadingly whole figure. */
const fmtDecimal: Fmt = (n) => n.toFixed(1)

/**
 * Home's time-window picker (round 12, Maroun: "also a picker for time: 1
 * hour, day, week, month, custom"). Kept in the URL (`?window=wk` or
 * `?from=&to=`) so a selection survives reload and can be shared, and
 * mirrored to localStorage as a convenience default for next time -- the
 * URL always wins on load, matching how `tabFromPath` already treats the
 * URL as the source of truth for which tab is open.
 */
type WindowPickerKey = '1h' | 'day' | 'wk' | 'mo' | 'custom'
interface ActivitySelection {
  key: WindowPickerKey
  /** ISO timestamps, custom only. Typed via `datetime-local` inputs, which
   *  carry no timezone of their own -- read as the BROWSER's local time,
   *  which for CHU staff devices is Sydney. A real timezone picker is a
   *  reasonable follow-up if that assumption turns out wrong for someone. */
  from?: string
  to?: string
}

const ACTIVITY_WINDOW_STORAGE_KEY = 'chu-gpt-portal-window'
const WINDOW_PICKER_OPTIONS: Array<[WindowPickerKey, string]> = [
  ['1h', 'Last hour'],
  ['day', 'Today'],
  ['wk', 'This week'],
  ['mo', 'This month'],
  ['custom', 'Custom'],
]

function isWindowPickerKey(v: unknown): v is WindowPickerKey {
  return v === '1h' || v === 'day' || v === 'wk' || v === 'mo' || v === 'custom'
}

function selectionFromLocation(): ActivitySelection | null {
  const params = new URLSearchParams(window.location.search)
  const from = params.get('from')
  const to = params.get('to')
  if (from && to) return { key: 'custom', from, to }
  const w = params.get('window')
  return w && isWindowPickerKey(w) && w !== 'custom' ? { key: w } : null
}

function selectionFromStorage(): ActivitySelection | null {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_WINDOW_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ActivitySelection>
    if (!isWindowPickerKey(parsed.key)) return null
    if (parsed.key === 'custom') return parsed.from && parsed.to ? { key: 'custom', from: parsed.from, to: parsed.to } : null
    return { key: parsed.key }
  } catch {
    return null
  }
}

/** The URL wins over the remembered choice, which wins over the default --
 *  same precedence `tabFromPath` already applies to which tab opens. */
function initialActivitySelection(): ActivitySelection {
  return selectionFromLocation() ?? selectionFromStorage() ?? { key: 'mo' }
}

function activityQueryString(sel: ActivitySelection): string {
  if (sel.key === 'custom' && sel.from && sel.to) {
    return `?from=${encodeURIComponent(sel.from)}&to=${encodeURIComponent(sel.to)}`
  }
  return `?window=${sel.key === 'custom' ? 'mo' : sel.key}`
}

/** The window `/api/leaderboard?window=` should use for the SAME selection
 *  -- reusing that endpoint's own vocabulary (round 12) rather than a
 *  second one. Custom has no equivalent there, so the boards stay on the
 *  calendar month, exactly what the backend's own `boardWindowKey: null`
 *  already means for the charts. */
function boardWindowKeyFor(sel: ActivitySelection): '1h' | 'day' | 'wk' | 'mo' {
  return sel.key === 'custom' ? 'mo' : sel.key
}

type HeatmapMode = 'you' | 'team' | 'company'
const HEATMAP_MODES: Array<[HeatmapMode, string]> = [
  ['you', 'You'],
  ['team', 'Your team'],
  ['company', 'Company'],
]

/** The Home activity tile's You/Your team/Company toggle (round 12, Maroun:
 *  "cool to overlay your activity with your team's and company's"). A
 *  toggle rather than a true overlay or three simultaneous small
 *  multiples: Maroun also asked for this SAME heatmap to be "large" with
 *  "cells large enough to read the numbers", and three datasets drawn at
 *  once in one tile can only be large by giving up that -- either three
 *  small-multiple grids at a third of the width each, or markers crowded
 *  onto cells already sized for one number. Switching keeps every view at
 *  full size and still answers the comparison question (does my pattern
 *  differ from my team's), one look at a time. */
function HeatmapToggle({ mode, setMode }: { mode: HeatmapMode; setMode: (m: HeatmapMode) => void }) {
  return (
    <div style={{ display: 'flex', gap: '0.25rem' }}>
      {HEATMAP_MODES.map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => setMode(id)}
          style={{
            fontSize: '0.68rem',
            fontWeight: 600,
            padding: '0.2rem 0.55rem',
            borderRadius: '999px',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--a-fb-font)',
            background: mode === id ? 'var(--a-accent)' : 'transparent',
            color: mode === id ? 'var(--a-on-accent)' : 'var(--a-ink-dim)',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

/**
 * Home's own time-window picker, above the charts (round 12). Drives every
 * time-based chart on the tab (the usage line, the donut, models, the
 * activity heatmap and its team/company comparisons, topics, MCP usage) and
 * the two boards below them, all from the ONE selection here -- see
 * `activityQueryString`/`boardWindowKeyFor`. Custom reveals two
 * `datetime-local` inputs rather than firing on every keystroke.
 */
function WindowPicker({ selection, onChange }: { selection: ActivitySelection; onChange: (s: ActivitySelection) => void }) {
  const [customFrom, setCustomFrom] = useState(selection.from ?? '')
  const [customTo, setCustomTo] = useState(selection.to ?? '')

  function toInputValue(iso: string | undefined): string {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    // `datetime-local` wants 'YYYY-MM-DDTHH:MM' in the BROWSER's own local
    // time, no trailing seconds/zone -- toISOString() is UTC, so this reads
    // it back out via the local getters rather than string-slicing UTC.
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center', marginBottom: '1rem' }}>
      {WINDOW_PICKER_OPTIONS.map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id === 'custom' ? { key: 'custom', from: customFrom, to: customTo } : { key: id })}
          style={pillButtonStyle(selection.key === id)}
        >
          {label}
        </button>
      ))}
      {selection.key === 'custom' ? (
        <span style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.78rem', color: 'var(--a-ink-dim)' }}>
          <input
            type="datetime-local"
            defaultValue={toInputValue(customFrom)}
            onChange={(e) => setCustomFrom(e.target.value ? new Date(e.target.value).toISOString() : '')}
            style={{ fontSize: '0.78rem', padding: '0.3rem 0.4rem', borderRadius: 'var(--a-radius-sm)', border: '1px solid var(--a-line)', background: 'var(--a-panel)', color: 'var(--a-ink)' }}
          />
          <span>to</span>
          <input
            type="datetime-local"
            defaultValue={toInputValue(customTo)}
            onChange={(e) => setCustomTo(e.target.value ? new Date(e.target.value).toISOString() : '')}
            style={{ fontSize: '0.78rem', padding: '0.3rem 0.4rem', borderRadius: 'var(--a-radius-sm)', border: '1px solid var(--a-line)', background: 'var(--a-panel)', color: 'var(--a-ink)' }}
          />
          <button
            type="button"
            disabled={!customFrom || !customTo}
            onClick={() => onChange({ key: 'custom', from: customFrom, to: customTo })}
            style={{ ...pillButtonStyle(true), opacity: !customFrom || !customTo ? 0.5 : 1 }}
          >
            Apply
          </button>
        </span>
      ) : null}
    </div>
  )
}

/** The bento tile: a flat white card, the same surface/line/radius/shadow
 *  every bento skeleton card uses (`arcade/skeletons.tsx`'s `.acard`/`.pcard`
 *  family), reproduced here as inline styles reading the SAME `--a-*`
 *  variables rather than the leaderboard's own scoped class names, which are
 *  written against the board's specific markup. */
/** `height: '100%'` + `justifyContent: 'center'`: on Home, the four stat
 *  tiles sit in one grid row under `align-items: stretch`, so the row's own
 *  height (set by whichever tile's content is tallest) is something every
 *  tile must actually FILL, not just receive as a taller box with the same
 *  content stuck at the top -- that was "Your rank" wrapping to two lines
 *  and the row visibly uneven at three different heights (round 11). Neutral
 *  in the Developer key tab's plain flex rows, which already stretch by the
 *  flexbox default. */
function Tile({
  label,
  value,
  sub,
  noTruncate,
  minWidth,
}: {
  label: string
  value: string
  sub?: string | undefined
  /** Round 13, Maroun's screenshot: the alias tile ellipsized
   *  "dev-maroun.ma..." at 1470 wide -- an identifier the person has to
   *  copy must never truncate. Set on any tile whose value is copied or
   *  typed elsewhere, never on a figure or a label where a shorter read is
   *  fine. Wraps instead of clipping, and widens the tile's own minimum so
   *  wrapping is the exception rather than the rule. */
  noTruncate?: boolean
  minWidth?: string
}) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: minWidth ?? '12rem',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        background: 'var(--a-panel)',
        border: '1px solid var(--a-line)',
        borderRadius: 'var(--a-radius)',
        boxShadow: 'var(--a-shadow)',
        padding: '1rem 1.2rem',
      }}
    >
      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--a-ink-dim)' }}>{label}</div>
      <div
        title={value}
        style={
          noTruncate
            ? { fontSize: '1.1rem', fontWeight: 700, marginTop: '0.3rem', color: 'var(--a-ink)', fontFamily: 'var(--a-fd-font)', overflowWrap: 'anywhere', lineHeight: 1.3 }
            : { fontSize: '1.35rem', fontWeight: 700, marginTop: '0.3rem', color: 'var(--a-ink)', fontFamily: 'var(--a-fd-font)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
        }
      >
        {value}
      </div>
      {sub ? <div style={{ fontSize: '0.78rem', color: 'var(--a-ink-dim)', marginTop: '0.2rem' }}>{sub}</div> : null}
    </div>
  )
}

function pillButtonStyle(active: boolean): CSSProperties {
  return {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: active ? 'var(--a-on-accent)' : 'var(--a-ink-dim)',
    background: active ? 'var(--a-accent)' : 'transparent',
    border: 'none',
    padding: '0.4rem 0.8rem',
    borderRadius: '999px',
    cursor: 'pointer',
    fontFamily: 'var(--a-fb-font)',
  }
}

function TabBar({
  tab,
  setTab,
  account,
  onSignOut,
  isAdmin,
}: {
  tab: PortalTab
  setTab: (t: PortalTab) => void
  account: AccountInfo | undefined
  onSignOut: () => void
  isAdmin: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1.4rem',
        background: 'var(--a-panel)',
        border: '1px solid var(--a-line)',
        borderRadius: 'var(--a-radius)',
        boxShadow: 'var(--a-shadow)',
        marginBottom: '1.4rem',
        // Full page width (Maroun, 24 Sep 2026: "can everything render to
        // the width of the page"), the same as the content grid below it.
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
        <OperatorAvatar
          account={account}
          style={{
            // Round 13 design review, finding 4: matched to the admin
            // header's own OperatorAvatar sizing (components/AppShell.tsx's
            // `styles.avatar`) -- the same shared component, same kind of
            // header position, had drifted to a larger size here with
            // nothing else on this header at a larger scale to justify it.
            width: '1.9rem',
            height: '1.9rem',
            borderRadius: '9999px',
            objectFit: 'cover',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.72rem',
            fontWeight: 700,
          }}
        />
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--a-ink)', fontFamily: 'var(--a-fd-font)' }}>
            {account?.name ?? account?.username ?? 'CHU-GPT'}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--a-ink-dim)' }}>CHU-GPT</div>
        </div>
        {/* Round 16, Unit 17(a): the portal is where a welcome email lands
            someone, and until this button existed there was no way through
            from here to the chat app itself -- first thing after the name,
            since it is the reason most people are on this page at all. */}
        <a
          href="https://chugpt.apps.chu.com.au"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0.5rem 1.1rem',
            borderRadius: '999px',
            background: 'var(--a-accent)',
            color: 'var(--a-on-accent)',
            fontWeight: 700,
            fontSize: '0.82rem',
            fontFamily: 'var(--a-fb-font)',
            textDecoration: 'none',
            marginLeft: '0.6rem',
          }}
        >
          Open CHU-GPT
        </a>
      </div>
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        {/* Round 13, Maroun: remove the Leaderboard tab from the pill for
            everyone. The page itself stays reachable at /leaderboard --
            tabFromPath/pathForTab and the render branch below are untouched
            -- it just has no menu entry here any more; it moves into the
            admin console's own nav instead (a later round). */}
        {(
          [
            ['home', 'Home'],
            ['guides', 'Guides'],
            ['budget', 'Budget'],
            ['devkey', 'Developer key'],
          ] as Array<[PortalTab, string]>
        ).map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)} style={pillButtonStyle(tab === id)}>
            {label}
          </button>
        ))}
        {isAdmin ? (
          <a
            href={
              window.location.hostname.startsWith('arcade.')
                ? `${window.location.protocol}//${window.location.hostname.replace(/^arcade\./, 'admin.')}/overview`
                : '/overview'
            }
            style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--a-ink-dim)', textDecoration: 'none', marginLeft: '0.3rem' }}
          >
            Admin console
          </a>
        ) : null}
        <button
          type="button"
          onClick={onSignOut}
          style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--a-hot)', background: 'transparent', border: 'none', cursor: 'pointer', marginLeft: '0.3rem' }}
        >
          Sign out
        </button>
      </div>
    </div>
  )
}

/** The bento panel: a wider tile for a chart or a list, same surface as
 *  `Tile` above but full-width and left-aligned rather than a single stat. */
/** `noMargin`: true inside a CSS grid cell, where the grid's own `gap`
 *  spaces panels apart and a second top margin would only add uneven space
 *  on top of it. False everywhere Panels stack in plain document flow
 *  (the Developer key tab), which is unchanged. */
/**
 * `height: '100%'` + `display: flex; flexDirection: column`: harmless where
 * Panel sits in normal document flow with no defined parent height (a
 * percentage height resolves to `auto` there, per CSS, which is exactly the
 * Developer Key tab's existing stacked layout) and what lets two Panels
 * stacked in ONE grid cell (round 12's models+topics pair, beside a big
 * heatmap) actually fill the height `align-items: stretch` gives that cell,
 * split evenly via `flex: 1` on each, rather than sizing to content and
 * leaving the difference as dead space -- the exact round-11 bug, one level
 * deeper. `overflow: hidden` is the same "never push out of the tile"
 * backstop `ChartFrame` already uses elsewhere.
 *
 * `headerRight`: an optional control (round 12's You/Team/Company toggle)
 * beside the title, never a second title style to keep in sync with this one.
 */
function Panel({
  title,
  children,
  noMargin = false,
  headerRight,
}: {
  title: string
  children: ReactNode
  noMargin?: boolean
  headerRight?: ReactNode
}) {
  return (
    <div
      style={{
        background: 'var(--a-panel)',
        border: '1px solid var(--a-line)',
        borderRadius: 'var(--a-radius)',
        boxShadow: 'var(--a-shadow)',
        padding: '1.1rem 1.3rem',
        marginTop: noMargin ? 0 : '1rem',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem', marginBottom: '0.6rem' }}>
        <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--a-ink-dim)' }}>{title}</span>
        {headerRight}
      </div>
      <div style={{ flex: '1 1 auto', minHeight: 0 }}>{children}</div>
    </div>
  )
}

/** One cell of the Home tab's 12-column bento grid -- `span` is out of 12.
 *  `minWidth: 0` on every cell is load-bearing: without it a wide SVG chart
 *  (the line chart, the heatmap) refuses to shrink below its natural width
 *  and blows the grid track out, which is the one bug a bento grid of
 *  variable-height content is prone to. `height: '100%'` is what makes
 *  `align-items: stretch` (below) actually visible rather than a no-op: a
 *  grid item stretches to its row's height by default, but the DIV inside it
 *  still needs telling to fill that box before `Tile`/`Panel` can do
 *  anything with the extra room. */
function GridCell({ span, children }: { span: number; children: ReactNode }) {
  return <div style={{ gridColumn: `span ${span}`, minWidth: 0, height: '100%' }}>{children}</div>
}

/**
 * Fixes one chart tile's content to a single height, shared by every tile in
 * its row -- round 11's actual bug. A Donut's viewBox is a SQUARE, so at a
 * 4-of-12-column width it rendered nearly 600px tall next to a line chart
 * scaled to ~260px, leaving the row's shorter tile with ~350px of dead
 * space beneath it. Centring content in a fixed-height box (rather than
 * trusting each chart's own aspect ratio to land near the others) is what
 * makes "same height across the row" true regardless of viewport width, and
 * `overflow: hidden` is the backstop for "never taller than its neighbours"
 * if a chart's natural content ever exceeds it.
 */
function ChartFrame({ height, children }: { height: number; children: ReactNode }) {
  return (
    <div style={{ height, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
      {children}
    </div>
  )
}

/** The Home tab's grid: 12 columns above the tablet breakpoint, one column
 *  (every cell full width, in the order they were written) below it. Written
 *  as a scoped stylesheet rather than inline styles because the breakpoint
 *  itself cannot be expressed as a React inline style.
 *
 *  `align-items: stretch` (round 11; was `start`): row 1's four stat tiles
 *  now match height automatically, whichever is tallest -- the alternative,
 *  hand-picking one fixed height for `Tile`, breaks the moment its content
 *  needs an extra line the tile was not sized for. Rows 2-3 do not depend on
 *  it (`ChartFrame` above already fixes their heights explicitly); stretch
 *  is harmless there since every cell already reports the same height.
 *
 *  `.home-board`: scopes the team/company board rows' overflow rule to
 *  Home's OWN boards, never the leaderboard's -- `.brow .val .surfsplit` is
 *  `white-space: nowrap` by design (arcadeCss.ts), correct in the
 *  leaderboard's own much wider board column, and the one thing that pushed
 *  a Home board row wider than its 6-of-12 tile before this rule capped it. */
const HOME_GRID_CSS = `
.home-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 1rem; align-items: stretch; }
.home-board .surfsplit { max-width: 190px; overflow: hidden; text-overflow: ellipsis; }
@media (max-width: 720px) {
  .home-grid { grid-template-columns: 1fr; }
  .home-grid > * { grid-column: 1 / -1 !important; }
}
`

/** The Home rank tile's MAIN line -- department position only, always one
 *  line. The org-wide position is `rankSubLine` below, in the tile's own
 *  smaller second-line slot (the same one the dev-key tile's cycle figure
 *  uses) rather than crammed onto the same line, which is what wrapped the
 *  tile to two lines and broke row 1's equal-height rule (round 11). */
function rankMainLine(rank: DepartmentRank): string {
  if (rank.orgRank === null) return 'No activity yet this month'
  return rank.department && rank.departmentRank !== null
    ? `#${rank.departmentRank} of ${rank.departmentTotal} in ${rank.department}`
    : 'Not yet ranked in a department'
}

function rankSubLine(rank: DepartmentRank): string | undefined {
  if (rank.orgRank === null) return undefined
  return `#${rank.orgRank} of ${rank.orgTotal} org-wide`
}

/** The person's own department, re-ranked 1..N within it (never their
 *  org-wide rank number, which would be meaningless on a team-only list) --
 *  the SAME `BoardPlayer[]` shape `BentoBoardRow` already renders, filtered
 *  from the one `/api/leaderboard` fetch rather than a second endpoint. */
function departmentBoard(board: BoardModel): { department: string | null; rows: BoardPlayer[] } {
  const department = board.you?.player.department ?? null
  if (!department) return { department: null, rows: [] }
  const rows = board.ranked.filter((p) => p.department === department).map((p, i) => ({ ...p, rank: i + 1 }))
  return { department, rows }
}

/** The top N of the company board, with the viewer's own row appended if
 *  they are not already in it -- never re-ranked, these keep their real
 *  org-wide position. */
function companyBoardTopN(board: BoardModel, n: number): BoardPlayer[] {
  const top = board.ranked.slice(0, n)
  if (board.you && !top.some((p) => p.id === board.you!.player.id)) {
    return [...top, board.you.player]
  }
  return top
}

/** `BentoBoardRow` wants a click handler; the portal has no profile
 *  drill-down of its own (that lives on the Leaderboard tab), so this is a
 *  deliberate no-op rather than a half-built modal. */
function noSelect(): void {}

function HomeTab({
  summary,
  activity,
  board,
  theme,
  windowSelection,
  onWindowChange,
  onOpenDevKey,
  onOpenPolicy,
  policy,
  onPolicyAccepted,
}: {
  summary: MeSummary | null
  activity: MeActivity | null
  board: BoardModel | null
  theme: ArcadeTheme
  windowSelection: ActivitySelection
  onWindowChange: (s: ActivitySelection) => void
  onOpenDevKey: () => void
  /** Opens the policy in THIS portal's Guides tab. A plain link to
   *  /guides/policy left the portal whenever it was mounted under /arcade on
   *  the admin host, landing in the admin console's own Guides route. */
  onOpenPolicy: () => void
  /** Round 15, Unit 13(b). `null` means "not loaded yet" and renders
   *  nothing -- the same withhold-rather-than-guess rule every other Home
   *  tile already follows for a summary/activity/board that has not
   *  arrived, so a slow read never flashes a wrong state first. */
  policy: PolicyStatus | null
  onPolicyAccepted: () => void
}) {
  // Before the early return below: React's hook-order rule, and this is the
  // one piece of UI state Home itself owns (round 12's You/Team/Company
  // heatmap toggle).
  const [heatmapMode, setHeatmapMode] = useState<HeatmapMode>('you')
  const [showPolicyModal, setShowPolicyModal] = useState(false)

  if (!summary) return <p style={{ color: 'var(--a-ink-dim)', textAlign: 'center' }}>Loading…</p>
  const chatValue = chatCycleLine(summary.chat)
  const devKeyValue =
    summary.devKey.status === 'active'
      ? `Active, ${summary.devKey.alias}`
      : summary.devKey.status === 'eligible'
        ? 'Not minted yet'
        : 'Not eligible'

  const usage = activity?.usage
  const hideCosts = summary.hideCosts
  const format = hideCosts ? fmtInt : fmtMoney
  const value = (spend: number | null, tokens: number) => (hideCosts ? tokens : (spend ?? 0))

  const dailySeries: LineSeries[] =
    usage && usage.splitAvailable
      ? [
          { name: 'Chat', values: usage.daily.map((d) => value(d.chatSpend, d.chatTokens)), color: theme.chartColors[0] ?? theme.accent },
          { name: 'Claude Code', values: usage.daily.map((d) => value(d.codeSpend, d.codeTokens)), color: theme.chartColors[1] ?? theme.cyan },
        ]
      : usage
        ? [
            {
              name: 'All usage (split unavailable)',
              values: usage.daily.map((d) => value(d.chatSpend, d.chatTokens) + value(d.codeSpend, d.codeTokens)),
              color: theme.chartColors[0] ?? theme.accent,
              muted: true,
            },
          ]
        : []
  // The backend already shortens `day` to the label the picker's bucket
  // unit calls for -- 'MM-DD', 'HH24:00' or 'HH24:MI' (round 12) -- so this
  // used to `.slice(5)` a full 'YYYY-MM-DD' and must not any more: slicing a
  // short label would cut a real digit off, not a year.
  const xLabels = usage ? usage.daily.map((d) => d.day) : []

  const donutSegments = usage
    ? usage.splitAvailable
      ? [
          { label: 'Chat', value: value(usage.totals.chatSpend, usage.totals.chatTokens) },
          { label: 'Claude Code', value: value(usage.totals.codeSpend, usage.totals.codeTokens) },
        ]
      : [
          {
            label: 'All usage',
            value: value(usage.totals.chatSpend, usage.totals.chatTokens) + value(usage.totals.codeSpend, usage.totals.codeTokens),
          },
        ]
    : []
  const donutTotal = donutSegments.reduce((n, s) => n + s.value, 0)

  // Capped at 6 rows (round 11): the backend already sends up to 8, and a
  // ranked-bars tile fixed to one row height (`ChartFrame`) has no room to
  // grow with the list -- "N more" says the rest exist rather than the tile
  // silently truncating with no sign anything was cut.
  const MODEL_ROWS_VISIBLE = 6
  const allModelRows: BarRow[] = usage ? usage.models.map((m) => ({ label: m.model, value: value(m.spend, m.tokens) })) : []
  const modelRows = allModelRows.slice(0, MODEL_ROWS_VISIBLE)
  const moreModels = Math.max(0, allModelRows.length - MODEL_ROWS_VISIBLE)

  // Three datasets for the same tile (round 12's You/Team/Company toggle),
  // never blended: "you" is a raw request count, team/company are already
  // "requests per person" from the backend (`ComparisonHeatmap.unit`), so
  // each needs its own formatter -- picking the wrong one would print a
  // fraction as though it were a whole request or vice versa.
  const youHeatCells: HeatCell[] = usage ? usage.hourly.map((h) => ({ row: h.dow, col: hourBucketLabel(h.hour), value: h.requests })) : []
  const teamHeatCells: HeatCell[] = usage ? usage.teamHeatmap.cells.map((c) => ({ row: c.dow, col: hourBucketLabel(c.hour), value: c.value })) : []
  const companyHeatCells: HeatCell[] = usage ? usage.companyHeatmap.cells.map((c) => ({ row: c.dow, col: hourBucketLabel(c.hour), value: c.value })) : []
  const heatmapView =
    heatmapMode === 'you'
      ? { cells: youHeatCells, format: fmtInt, unit: 'requests', available: true }
      : heatmapMode === 'team'
        ? { cells: teamHeatCells, format: fmtDecimal, unit: 'requests per person', available: (usage?.teamHeatmap.headcount ?? 0) > 0 }
        : { cells: companyHeatCells, format: fmtDecimal, unit: 'requests per person', available: (usage?.companyHeatmap.headcount ?? 0) > 0 }

  const team = board ? departmentBoard(board) : { department: null, rows: [] }
  const companyTop = board ? companyBoardTopN(board, 8) : []

  return (
    <div>
      <style>{HOME_GRID_CSS}</style>
      {/* Round 15, Unit 13(b): a standing panel, INDEPENDENT of the policy
          gate switch -- see loadPolicy's own comment in StaffPortalShell.
          `policy === null` (not loaded yet) renders nothing, the same
          withhold-rather-than-guess rule every other Home tile follows. */}
      {policy && policy.accepted ? (
        <div
          style={{
            background: 'var(--a-panel)',
            border: '1px solid var(--a-line)',
            borderRadius: 'var(--a-radius)',
            padding: '1rem 1.2rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ color: 'var(--a-ink)', fontSize: '0.92rem' }}>
            AI usage policy {policy.acceptedVersion} accepted on {fmtDate(policy.acceptedAt)}.{' '}
            <a
              href="/guides/policy"
              style={{ color: 'var(--a-accent)' }}
              onClick={(e) => {
                // A modified click (new tab, new window) keeps the browser's
                // own behaviour; a plain click stays inside the portal.
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                e.preventDefault()
                onOpenPolicy()
              }}
            >
              Read it again
            </a>
            .
          </span>
        </div>
      ) : policy && !policy.accepted ? (
        /* Round 16, Unit 17(b): the hero of the page until accepted, not a
           thin bordered strip -- Maroun's own test with a real account
           found the strip easy to miss entirely. Same mascot as the
           welcome email's hero band, unnamed for the same reason
           (onboardingEmail.ts's own header comment), and the same
           speech-bubble shape. Collapses to the one-line accepted state
           above the moment `onPolicyAccepted` refetches. */
        <div
          style={{
            background: 'var(--a-panel2)',
            border: '2px solid var(--a-accent)',
            borderRadius: 'var(--a-radius)',
            padding: '1.6rem 2rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap',
          }}
        >
          <img src={peekMascot} alt="" style={{ width: '96px', height: 'auto', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: '16rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            <div
              style={{
                background: 'var(--a-panel)',
                border: '1px solid var(--a-line)',
                borderRadius: '4px 16px 16px 16px',
                padding: '0.9rem 1.1rem',
                color: 'var(--a-ink)',
                fontSize: '0.98rem',
                fontWeight: 600,
              }}
            >
              Before you use CHU-GPT, read and accept the AI usage policy. It takes a minute.
            </div>
            <button
              type="button"
              onClick={() => setShowPolicyModal(true)}
              style={{
                alignSelf: 'flex-start',
                padding: '0.65rem 1.4rem',
                borderRadius: '999px',
                border: 'none',
                background: 'var(--a-accent)',
                color: 'var(--a-on-accent)',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
                fontFamily: 'var(--a-fb-font)',
              }}
            >
              Read and accept
            </button>
          </div>
        </div>
      ) : null}
      {showPolicyModal ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--a-veil)' }}>
          <PolicyGateScreen
            onAccepted={() => {
              setShowPolicyModal(false)
              onPolicyAccepted()
            }}
            onCancel={() => setShowPolicyModal(false)}
          />
        </div>
      ) : null}
      <WindowPicker selection={windowSelection} onChange={onWindowChange} />
      <div className="home-grid">
        {/* ---- ROW 1: the four stat tiles, 3 columns each. "This month's
             chat usage" and the dev-key cycle figure stay MONTHLY regardless
             of the picker above (they describe the budget cycle and the
             chat cap, not a time-series a person can rewind) -- their
             labels already say so. ---- */}
        <GridCell span={3}>
          <Tile label="This month's chat usage" value={chatValue} />
        </GridCell>
        <GridCell span={3}>
          <Tile
            label="Developer key"
            value={devKeyValue}
            sub={summary.devKey.status === 'active' ? devKeyCycleLine(summary.devKey.spend, summary.devKey.budget) : undefined}
          />
        </GridCell>
        <GridCell span={3}>
          <Tile
            label="Your rank"
            value={activity ? rankMainLine(activity.rank) : 'Loading…'}
            sub={activity ? rankSubLine(activity.rank) : undefined}
          />
        </GridCell>
        <GridCell span={3}>
          <Tile label="Guides" value="Hub & Prompt Library" sub="Linked from your welcome email" />
        </GridCell>

        {/* ---- ROW 2: the 30-day trend (8 cols) and the channel donut (4),
             both fixed to the same 300px content height (round 11: a Donut's
             square viewBox otherwise renders far taller than the line chart
             next to it, leaving the row broken and the shorter tile padded
             with dead space). ---- */}
        <GridCell span={8}>
          <Panel title="Your usage, last 30 days" noMargin>
            <ChartFrame height={300}>
              <div style={{ width: '100%' }}>
                <LineChart series={dailySeries} xLabels={xLabels} format={format} height={250} label="Usage over the last 30 days" />
                {/* Every named (non-muted) series gets its own legend entry --
                    the chart's own end-of-line label is not a substitute; two
                    series with only one labelled is the bug this fixes. */}
                <ChartLegend items={dailySeries.filter((s) => !s.muted).map((s) => ({ label: s.name, color: s.color }))} />
              </div>
            </ChartFrame>
          </Panel>
        </GridCell>
        <GridCell span={4}>
          <Panel title="This month by channel" noMargin>
            <ChartFrame height={300}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                {/* Capped at 200px regardless of the tile's own width -- a
                    Donut's viewBox is a square, so left to `width: 100%` it
                    grows exactly as tall as the tile is wide. */}
                <div style={{ width: 200 }}>
                  <Donut
                    segments={donutSegments}
                    colors={theme.chartColors}
                    hero={format(donutTotal)}
                    heroLabel="this month"
                    label="This month by channel"
                  />
                </div>
                {donutSegments.length > 0 ? (
                  <div style={{ marginTop: '0.5rem' }}>
                    <ChartLegend items={donutSegments.map((s, i) => ({ label: s.label, color: theme.chartColors[i] ?? theme.accent }))} />
                  </div>
                ) : null}
                {usage ? (
                  <p style={{ fontSize: '0.72rem', color: 'var(--a-ink-dim)', marginTop: '0.5rem', marginBottom: 0, textAlign: 'center' }} title={usage.desktop.reason}>
                    Desktop: not yet measured (hover for why)
                  </p>
                ) : null}
              </div>
            </ChartFrame>
          </Panel>
        </GridCell>

        {/* ---- ROW 3 (round 12, Maroun: "would be nice to have the
             when-you're-active graph large, and would be cool to overlay
             your activity with your team's and company's"): the heatmap
             takes 8 columns at a genuinely large, readable size; models and
             topics share the remaining 4 columns, stacked and each taking
             half that height (Panel's own `flex: 1` on a stretched grid
             cell, not a guessed pixel split). ---- */}
        <GridCell span={8}>
          <Panel title="When you're active" noMargin headerRight={<HeatmapToggle mode={heatmapMode} setMode={setHeatmapMode} />}>
            <ChartFrame height={360}>
              <div style={{ width: '100%' }}>
                {heatmapView.available ? (
                  <Heatmap
                    rows={WEEKDAY_ROWS}
                    cols={HOUR_COLS}
                    cells={heatmapView.cells}
                    surface={theme.panel}
                    hue={theme.accent}
                    format={heatmapView.format}
                    unit={heatmapView.unit}
                    label={`Hour of day, last 30 days -- ${heatmapMode}`}
                    fillTile
                  />
                ) : (
                  <p className="hint">Not enough people to compare yet.</p>
                )}
              </div>
            </ChartFrame>
          </Panel>
        </GridCell>
        <GridCell span={4}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%' }}>
            <div style={{ flex: '1 1 0', minHeight: 0 }}>
              <Panel title="Models you used this month" noMargin>
                <div style={{ width: '100%' }}>
                  <RankedBars rows={modelRows} colors={theme.chartColors} format={format} label="Models used" />
                  {moreModels > 0 ? (
                    <p style={{ fontSize: '0.74rem', color: 'var(--a-ink-dim)', margin: '0.4rem 0 0', textAlign: 'center' }}>{moreModels} more</p>
                  ) : null}
                </div>
              </Panel>
            </div>
            <div style={{ flex: '1 1 0', minHeight: 0 }}>
              {/* Reuses the SAME taxonomy component the board's own profile
                  modal draws with (`arcade/atoms.tsx`'s `TopicShareBar`),
                  against this person's own topics this month -- never a
                  second chart built to look similar. */}
              <Panel title="Your topics this month" noMargin>
                {activity && !activity.taxonomy.available ? (
                  <p className="hint">Daily data, not available for this window.</p>
                ) : (
                  <TopicShareBar topics={activity?.taxonomy.month ?? []} colors={theme.chartColors} />
                )}
              </Panel>
            </div>
          </div>
        </GridCell>

        {/* ---- ROW 4: your team board (6 cols), the company board (6). Both
             reuse `BentoBoardRow` -- the arcade's own leaderboard row -- over
             the SAME `/api/leaderboard` (tokens mode) fetch, never a second
             ranking implementation. `tokensOnly` (round 11, Maroun: "tokens
             only") drops the dollar figure `SurfaceBreakdown` would otherwise
             print beside the chat/Claude Code token split; `.home-board`
             (HOME_GRID_CSS) is what stops that split's own long, deliberately
             `white-space: nowrap` text (correct in the leaderboard's own
             wider column) from pushing a narrower Home tile wider than it is. ---- */}
        <GridCell span={6}>
          <Panel title={team.department ? `Your team · ${team.department}` : 'Your team'} noMargin>
            {!board ? (
              <p className="hint">Loading…</p>
            ) : team.rows.length === 0 ? (
              <p className="hint">No activity yet this window.</p>
            ) : (
              <div className="blist home-board">
                {team.rows.map((p) => (
                  <BentoBoardRow key={p.id} p={p} model={board} onSelectPlayer={noSelect} tokensOnly />
                ))}
              </div>
            )}
          </Panel>
        </GridCell>
        <GridCell span={6}>
          <Panel title="Company leaderboard · tokens" noMargin>
            {!board ? (
              <p className="hint">Loading…</p>
            ) : companyTop.length === 0 ? (
              <p className="hint">No activity yet this window.</p>
            ) : (
              <div className="blist home-board">
                {companyTop.map((p) => (
                  <BentoBoardRow key={p.id} p={p} model={board} onSelectPlayer={noSelect} tokensOnly />
                ))}
              </div>
            )}
          </Panel>
        </GridCell>

        {/* ---- ROW 5: MCP usage, full width. ---- */}
        <GridCell span={12}>
          <Panel title="Your MCP usage this month" noMargin>
            {activity && !activity.mcp.available ? (
              <p className="hint">Daily data, not available for this window.</p>
            ) : !activity || activity.mcp.servers.length === 0 ? (
              <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.86rem', margin: 0 }}>No tool calls recorded this month.</p>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {activity.mcp.servers.map((s) => (
                    <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.86rem', color: 'var(--a-ink)' }}>
                      <span>{s.displayName}</span>
                      <span style={{ color: 'var(--a-ink-dim)', fontFamily: 'ui-monospace, monospace' }}>{s.calls.toLocaleString()} calls</span>
                    </div>
                  ))}
                </div>
                {activity.mcp.topTools.length > 0 ? (
                  <div style={{ marginTop: '0.9rem', paddingTop: '0.7rem', borderTop: '1px solid var(--a-line)' }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--a-ink-dim)', marginBottom: '0.4rem' }}>
                      Your top tools
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      {activity.mcp.topTools.map((t) => (
                        <div key={`${t.id}::${t.tool}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: 'var(--a-ink-dim)' }}>
                          <span>
                            {t.displayName} <span style={{ fontFamily: 'ui-monospace, monospace' }}>{t.tool}</span>
                          </span>
                          <span style={{ fontFamily: 'ui-monospace, monospace' }}>{t.calls.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </Panel>
        </GridCell>
      </div>

      {summary.devKey.status !== 'not_eligible' ? (
        <button
          type="button"
          onClick={onOpenDevKey}
          style={{
            marginTop: '1rem',
            padding: '0.6rem 1.2rem',
            borderRadius: '999px',
            border: 'none',
            background: 'var(--a-accent)',
            color: 'var(--a-on-accent)',
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'var(--a-fb-font)',
          }}
        >
          {summary.devKey.status === 'active' ? 'Manage my developer key' : 'Set up my developer key'}
        </button>
      ) : null}
    </div>
  )
}

/**
 * Budget requests: no longer a mailto (round 10, unit B). Raising a request
 * writes a row the admin console's Budget requests page queues, and every
 * state change -- created, decided, applied, notified -- is its own
 * immutable event (`clients/budgetRequests.ts`, backend). This portal-side
 * shape mirrors that table + event row exactly; nothing here computes a
 * status, it only displays what the backend already decided.
 */
export interface BudgetRequestEvent {
  id: number
  requestId: number
  at: string
  actorEmail: string
  actorRole: 'user' | 'admin'
  action: 'created' | 'withdrawn' | 'approved' | 'rejected' | 'budget_applied' | 'apply_failed' | 'notified' | 'notify_failed'
  fromStatus: string | null
  toStatus: string | null
  requestedBudgetUsd: number | null
  approvedBudgetUsd: number | null
  note: string | null
}

export interface BudgetRequest {
  id: number
  email: string
  /** Missing on any row from before 25 Sep 2026 -- means 'devkey', the
   *  only kind of request that existed then. */
  kind?: 'devkey' | 'chat'
  keyAlias: string
  currentBudgetUsd: number | null
  requestedBudgetUsd: number
  justification: string
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn'
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  approvedBudgetUsd: number | null
  createdAt: string
  events: BudgetRequestEvent[]
}

const BUDGET_REQUEST_ACTION_LABEL: Record<BudgetRequestEvent['action'], string> = {
  created: 'You submitted this request',
  withdrawn: 'You withdrew this request',
  approved: 'Approved',
  rejected: 'Not approved',
  budget_applied: 'Your new budget is live',
  apply_failed: 'The new budget did not reach your key yet (ask your admin)',
  notified: 'You were emailed',
  notify_failed: 'The notification email failed to send',
}

/** The timeline any one request shows, oldest first, exactly as the
 *  backend's `listEvents` already orders it -- never re-sorted here. */
function BudgetRequestTimeline({ events }: { events: BudgetRequestEvent[] }) {
  return (
    <ol style={{ listStyle: 'none', margin: '0.5rem 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      {events.map((e) => {
        const failed = e.action === 'apply_failed' || e.action === 'notify_failed'
        return (
          <li key={e.id} style={{ fontSize: '0.8rem', paddingLeft: '0.6rem', borderLeft: `2px solid ${failed ? 'var(--a-hot)' : 'var(--a-line)'}`, color: failed ? 'var(--a-hot)' : 'var(--a-ink)' }}>
            <div>{BUDGET_REQUEST_ACTION_LABEL[e.action]}</div>
            <div style={{ color: 'var(--a-ink-dim)' }}>{fmtDate(e.at)}</div>
            {e.approvedBudgetUsd !== null ? <div style={{ color: 'var(--a-ink-dim)' }}>New budget: {fmtMoney(e.approvedBudgetUsd)}</div> : null}
            {e.note && e.action !== 'created' ? <div style={{ color: 'var(--a-ink-dim)', whiteSpace: 'pre-wrap' }}>{e.note}</div> : null}
          </li>
        )
      })}
    </ol>
  )
}

/** Copy-to-clipboard for the export lines/config values below. Falls back to
 *  a visible "copy failed" rather than pretending it worked -- the whole
 *  point of the button is that the person did not have to re-type the value
 *  correctly by hand. */
function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setState('copied')
    } catch {
      setState('failed')
    }
    setTimeout(() => setState('idle'), 1500)
  }
  return (
    <button
      type="button"
      onClick={() => void copy()}
      style={{
        padding: '0.3rem 0.7rem',
        borderRadius: '999px',
        border: '1px solid var(--a-line)',
        background: 'transparent',
        color: state === 'failed' ? 'var(--a-hot)' : 'var(--a-ink-dim)',
        fontSize: '0.72rem',
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'var(--a-fb-font)',
      }}
    >
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : label}
    </button>
  )
}

const CLAUDE_CODE_EXPORT_LINES = [
  'export ANTHROPIC_BASE_URL=https://api.chugpt.apps.chu.com.au',
  'export ANTHROPIC_MODEL=claude-code:sonnet-5',
  'export ANTHROPIC_SMALL_FAST_MODEL=claude-code:haiku-4.5',
]

/** The exact `inferenceModels` aliases from `docs/claude-desktop-deployment-guide.md`
 *  section 4 -- the RAW gateway aliases the app's config needs, deliberately
 *  NOT the display-label `models` list above: that list is for reading, this
 *  one is for pasting into a JSON config, and the two must never be the same
 *  array. The set is fixed platform-wide (every Desktop deployment uses the
 *  same profile), not per-key, so this is not read from the API. */
const CLAUDE_DESKTOP_MODEL_ALIASES = ['claude-code:sonnet-5', 'claude-code:sonnet-5.5', 'claude-code:opus-5.5', 'claude-code:fable-5.1-offshore', 'claude-code:haiku-4.5']

/** A monospace block with one copy button per line AND one for the whole
 *  block -- matching the three lines the onboarding email already sends
 *  (`email/onboardingEmail.ts`'s `claudeCodeBlock`), never a second wording
 *  of the same instructions. */
function CodeBlock({ lines }: { lines: string[] }) {
  return (
    <div
      style={{
        background: '#06272E',
        color: '#DCEFE7',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: '0.8rem',
        borderRadius: '8px',
        padding: '0.8rem 0.9rem',
        marginTop: '0.5rem',
      }}
    >
      {lines.map((line) => (
        <div key={line} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', padding: '0.1rem 0' }}>
          <span style={{ wordBreak: 'break-all' }}>{line}</span>
          <CopyButton text={line} />
        </div>
      ))}
      <div style={{ marginTop: '0.4rem' }}>
        <CopyButton text={lines.join('\n')} label="Copy all" />
      </div>
    </div>
  )
}

/** One ceiling's current value and the highest a request may ask for --
 *  the shape `GET /api/me/budget-requests` returns for `chat`, and for
 *  `devKey` when the person holds one. */
export interface CeilingInfo {
  current: number
  max: number
}

/** Both ceilings a person might raise. `devKey` is `null` for anyone
 *  without a developer key, which is what lets the panel disable that
 *  option in the select rather than offering a number that means nothing --
 *  `chat` is never null: James Makin, 25 Sep 2026, had no key at all and
 *  every bit of a chat ceiling to raise. */
export interface BudgetCeilings {
  chat: CeilingInfo
  devKey: CeilingInfo | null
}

/** Which ceiling a request is about; `'chat'` is the panel's default
 *  selection, since it is the one ceiling everyone has. */
type RequestKind = 'chat' | 'devkey'

/**
 * Request-a-higher-ceiling: the create form when there is no open request,
 * the open request's own timeline + Withdraw when there is one, and past
 * decided requests below. Every state transition here is a call to
 * `routes/budgetRequests.ts` (backend); this component computes nothing
 * about status, it only renders what came back.
 *
 * SHOWN TO EVERYONE (25 Sep 2026, James Makin: no developer key at all, hit
 * his $100 chat ceiling, and this panel used to return `null` for exactly
 * that person -- the one case the platform most needed it to show up for).
 * The Ceiling select offers `chat` always and `devkey` only when
 * `ceilings.devKey` is not null, which is how the mock's own disabled
 * option ("Developer key ceiling (you have no key)") is expressed here.
 *
 * `ceilings` is READ from `GET /api/me/budget-requests`, never computed
 * here -- Round 13, Maroun's screenshot: this used to hardcode its OWN copy
 * of the platform floor ($500) as if it were the ceiling, so a $1000 key
 * was told "you can ask for up to $500.00", impossible, since a request
 * must also be MORE than the current budget. The backend's
 * `maxRequestableBudget` is the one place that number is computed, for
 * EACH ceiling separately; this component only displays it.
 */
function BudgetRequestPanel({
  requests,
  ceilings,
  ghostButtonStyle,
  primaryButtonStyle,
  onChanged,
}: {
  requests: BudgetRequest[] | null
  ceilings: BudgetCeilings | null
  ghostButtonStyle: CSSProperties
  primaryButtonStyle: CSSProperties
  onChanged: () => void
}) {
  const [kind, setKind] = useState<RequestKind>('chat')
  const [requestedBudget, setRequestedBudget] = useState('')
  const [justification, setJustification] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!ceilings) return null

  const selected = kind === 'chat' ? ceilings.chat : ceilings.devKey
  const currentBudget = selected?.current ?? 0
  const requestMaxBudget = selected?.max ?? null
  const pending = requests?.find((r) => r.status === 'pending') ?? null
  const decided = requests?.filter((r) => r.status !== 'pending') ?? []

  async function submit() {
    if (!selected) {
      // Cannot actually be reached from the UI (the option is disabled),
      // but a request must never go out naming a ceiling that does not
      // exist -- defensive, not decorative.
      setError('You do not have a developer key yet.')
      return
    }
    const requested = Number(requestedBudget)
    setError(null)
    if (!Number.isFinite(requested) || requested <= currentBudget) {
      setError(`Enter a number more than your current ceiling ($${currentBudget.toFixed(2)}).`)
      return
    }
    if (requestMaxBudget !== null && requested > requestMaxBudget) {
      setError(`Enter a number up to $${requestMaxBudget.toFixed(2)}.`)
      return
    }
    if (justification.trim().length < 20) {
      setError('Say a bit more about why (at least 20 characters).')
      return
    }
    setBusy(true)
    try {
      await apiJson('/api/me/budget-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, requestedBudget: requested, justification: justification.trim() }),
      })
      setRequestedBudget('')
      setJustification('')
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function withdraw(id: number) {
    setBusy(true)
    setError(null)
    try {
      await apiJson(`/api/me/budget-request/${id}/withdraw`, { method: 'POST' })
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel title="Request a higher ceiling">
      {error ? <p style={{ color: 'var(--a-hot)', fontSize: '0.84rem' }}>{error}</p> : null}
      {pending ? (
        <div>
          <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.86rem', margin: '0 0 0.5rem' }}>
            You have a pending request for {fmtMoney(pending.requestedBudgetUsd)}
            {pending.kind === 'chat' ? ' (chat ceiling)' : ' (developer key)'}.
          </p>
          <BudgetRequestTimeline events={pending.events} />
          <button type="button" disabled={busy} onClick={() => void withdraw(pending.id)} style={{ ...ghostButtonStyle, marginTop: '0.7rem' }}>
            Withdraw
          </button>
        </div>
      ) : (
        <div>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--a-ink)', marginBottom: '0.3rem' }}>
            Ceiling
          </label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as RequestKind)}
            style={{ width: '100%', padding: '0.55rem 0.8rem', borderRadius: 'var(--a-radius-sm)', border: '1px solid var(--a-line)', background: 'var(--a-panel)', color: 'var(--a-ink)', fontSize: '0.86rem', marginBottom: '0.6rem' }}
          >
            <option value="chat">{`Chat ceiling ($${ceilings.chat.current.toFixed(0)} now)`}</option>
            <option value="devkey" disabled={!ceilings.devKey}>
              {ceilings.devKey ? `Developer key ceiling ($${ceilings.devKey.current.toFixed(2)} now)` : 'Developer key ceiling (you have no key)'}
            </option>
          </select>
          <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.86rem', margin: '0 0 0.6rem' }}>
            Not self-service: an admin reviews every request. Your current ceiling is {fmtMoney(currentBudget)}; you can
            ask for more than {fmtMoney(currentBudget)} and up to{' '}
            {requestMaxBudget !== null ? fmtMoney(requestMaxBudget) : '…'} a month.
          </p>
          <input
            type="number"
            placeholder={
              requestMaxBudget !== null
                ? `New ceiling (more than $${currentBudget.toFixed(2)}, up to $${requestMaxBudget.toFixed(2)})`
                : `New ceiling (more than $${currentBudget.toFixed(2)})`
            }
            value={requestedBudget}
            onChange={(e) => setRequestedBudget(e.target.value)}
            style={{ width: '100%', padding: '0.55rem 0.8rem', borderRadius: 'var(--a-radius-sm)', border: '1px solid var(--a-line)', background: 'var(--a-panel)', color: 'var(--a-ink)', fontSize: '0.86rem', marginBottom: '0.5rem' }}
          />
          <textarea
            placeholder="Why do you need it? (at least 20 characters)"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
            style={{ width: '100%', padding: '0.55rem 0.8rem', borderRadius: 'var(--a-radius-sm)', border: '1px solid var(--a-line)', background: 'var(--a-panel)', color: 'var(--a-ink)', fontSize: '0.86rem', fontFamily: 'inherit', resize: 'vertical' }}
          />
          <button type="button" disabled={busy} onClick={() => void submit()} style={{ ...primaryButtonStyle, marginTop: '0.6rem' }}>
            Submit request
          </button>
        </div>
      )}

      {decided.length > 0 ? (
        <div style={{ marginTop: '1.1rem', paddingTop: '0.9rem', borderTop: '1px solid var(--a-line)' }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--a-ink-dim)', marginBottom: '0.6rem' }}>
            Past requests
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {decided.map((r) => (
              <div key={r.id}>
                <div style={{ fontSize: '0.84rem', color: 'var(--a-ink)' }}>
                  {fmtMoney(r.requestedBudgetUsd)} requested, {r.status}
                </div>
                <BudgetRequestTimeline events={r.events} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Panel>
  )
}

/** Shared by DeveloperKeyTab and BudgetTab (Round 16, Unit 18 split them
 *  out of one component into two) -- hoisted to module scope rather than
 *  redeclared in each, since they were byte-identical the moment there
 *  were two callers. */
const cardStyle: CSSProperties = {
  background: 'var(--a-panel)',
  border: '1px solid var(--a-line)',
  borderRadius: 'var(--a-radius)',
  boxShadow: 'var(--a-shadow)',
  padding: '1.5rem',
  // Full width, not a centred 64rem card (Maroun, 24 Sep 2026): the Budget
  // and Developer key tabs now line up with the header and nav above them.
}
const ghostButtonStyle: CSSProperties = {
  padding: '0.55rem 1.05rem',
  borderRadius: '999px',
  border: '1.5px solid var(--a-line)',
  background: 'transparent',
  color: 'var(--a-ink)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--a-fb-font)',
}
const primaryButtonStyle: CSSProperties = {
  padding: '0.6rem 1.2rem',
  borderRadius: '999px',
  border: 'none',
  background: 'var(--a-accent)',
  color: 'var(--a-on-accent)',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'var(--a-fb-font)',
}

/** The month name and "day Month" label for the NEXT reset -- shared by the
 *  banner text below. Client-local time is good enough for copy that says
 *  roughly when chat comes back, not a billing-accurate instant. */
function nextResetLabel(now: Date): string {
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  return `${next.getDate()} ${next.toLocaleString('en-AU', { month: 'long' })}`
}

/** Whether -- and what -- to say above the tiles about the chat ceiling.
 *  `null` well under the ceiling; `'near'` from 90% (a heads-up before it
 *  actually bites); `'over'` at or past it, wording lifted from the mock
 *  (`docs/design/mocks/2026-09-25-round18/3-chat-ceiling-request.html`).
 *  Pure and exported for its own test -- the one thing here worth testing
 *  in isolation from the fetch/render machinery around it. */
export function chatCeilingBanner(chat: ChatPosition, now: Date = new Date()): { tone: 'over' | 'near'; text: string } | null {
  if (chat.unlimited || chat.spend === null || chat.cap === null || chat.cap <= 0) return null
  const ratio = chat.spend / chat.cap
  const monthName = now.toLocaleString('en-AU', { month: 'long' })
  if (ratio >= 1) {
    return {
      tone: 'over',
      text: `You have reached your chat ceiling for ${monthName}. CHU-GPT chat will refuse new messages until ${nextResetLabel(now)}, or until a higher ceiling is approved.`,
    }
  }
  if (ratio >= 0.9) {
    return {
      tone: 'near',
      text: `You are close to your chat ceiling for ${monthName} (${fmtMoney(chat.spend)} of ${fmtMoney(chat.cap)} used). Chat will stop once you reach it.`,
    }
  }
  return null
}

function ChatCeilingBanner({ chat }: { chat: ChatPosition }) {
  const info = chatCeilingBanner(chat)
  if (!info) return null
  const over = info.tone === 'over'
  return (
    <div
      style={{
        background: over ? '#FFF2F1' : '#FFF8E5',
        border: `1px solid ${over ? '#EFC1BD' : '#E6D58A'}`,
        color: over ? '#8E2C25' : '#7A5D00',
        borderRadius: 'var(--a-radius-sm)',
        padding: '0.7rem 0.9rem',
        marginBottom: '1.1rem',
        fontWeight: 600,
        fontSize: '0.86rem',
      }}
    >
      {info.text}
    </div>
  )
}

/**
 * Budget: a banner when the chat ceiling is reached or close to it, chat
 * ceiling and this month's spend (everyone), the developer key's own
 * ceiling (key holders only), the request-a-higher-ceiling form and full
 * history (Round 16, Unit 18) -- moved off the Developer key tab, where
 * Maroun's own test with a real account could not find either.
 *
 * `BudgetRequestPanel` is shown to EVERYONE now (25 Sep 2026, James Makin):
 * it used to return null for anyone without an active developer key, which
 * hid the whole panel from exactly the person the platform most needed it
 * to show up for -- someone over their CHAT ceiling with no key at all.
 */
function BudgetTab({
  summary,
  budgetRequests,
  ceilings,
  onBudgetRequestsChanged,
}: {
  summary: MeSummary | null
  budgetRequests: BudgetRequest[] | null
  ceilings: BudgetCeilings | null
  onBudgetRequestsChanged: () => void
}) {
  if (!summary) return <p style={{ color: 'var(--a-ink-dim)', textAlign: 'center' }}>Loading…</p>

  return (
    <div style={cardStyle}>
      <h2 style={{ color: 'var(--a-ink)', marginTop: 0, fontFamily: 'var(--a-fd-font)' }}>Budget</h2>
      <ChatCeilingBanner chat={summary.chat} />
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
        <Tile label="Chat ceiling" value={chatCycleLine(summary.chat)} sub="This month" />
        {summary.devKey.status === 'active' ? (
          <Tile
            label="Developer key ceiling"
            value={devKeyCycleLine(summary.devKey.spend, summary.devKey.budget)}
            sub={summary.devKey.alias}
            noTruncate
          />
        ) : null}
      </div>
      <BudgetRequestPanel
        requests={budgetRequests}
        ceilings={ceilings}
        ghostButtonStyle={ghostButtonStyle}
        primaryButtonStyle={primaryButtonStyle}
        onChanged={onBudgetRequestsChanged}
      />
    </div>
  )
}

function DeveloperKeyTab({
  summary,
  detail,
  hideCosts,
  theme,
  onChanged,
}: {
  summary: MeSummary | null
  detail: DevKeyDetail | null
  hideCosts: boolean
  theme: ArcadeTheme
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [revealed, setRevealed] = useState<string | null>(null)
  /** Rotate and Revoke both fired on the FIRST click, with no way back --
   *  a stray click invalidated a working key mid-session, worse for revoke
   *  since nothing replaces it. This gates both behind an in-app confirm
   *  (never `window.confirm`: a native dialog cannot be driven by browser
   *  automation and does not carry the arcade theme), the same inline
   *  confirm-row shape `People.tsx`'s own key revoke already uses on the
   *  admin console -- documented, as of this fix, in
   *  `docs/guides/claude-code.md`'s marker 03. */
  const [confirmAction, setConfirmAction] = useState<'rotate' | 'revoke' | null>(null)

  async function mint() {
    setBusy(true)
    setError(null)
    try {
      const result = await apiJson<{ key: string }>('/api/me/dev-key', { method: 'POST' })
      setRevealed(result.key)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
      onChanged()
    }
  }

  async function rotate() {
    setConfirmAction(null)
    setBusy(true)
    setError(null)
    try {
      const result = await apiJson<{ key: string }>('/api/me/dev-key/rotate', { method: 'POST' })
      setRevealed(result.key)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
      onChanged()
    }
  }

  async function revoke() {
    setConfirmAction(null)
    setBusy(true)
    setError(null)
    try {
      await api('/api/me/dev-key/revoke', { method: 'POST' })
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
      onChanged()
    }
  }

  if (!summary) return <p style={{ color: 'var(--a-ink-dim)', textAlign: 'center' }}>Loading…</p>

  if (revealed) {
    return (
      <div style={cardStyle}>
        <h2 style={{ color: 'var(--a-ink)', marginTop: 0, fontFamily: 'var(--a-fd-font)' }}>Your developer key</h2>
        <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.84rem' }}>This is shown once. It will not be shown again after you leave this page.</p>
        <div
          style={{
            display: 'flex',
            gap: '0.6rem',
            alignItems: 'center',
            padding: '0.8rem 1rem',
            borderRadius: 'var(--a-radius-sm)',
            border: '1px dashed var(--a-accent)',
            background: 'var(--a-hover)',
            fontFamily: 'ui-monospace, monospace',
            color: 'var(--a-ink)',
            wordBreak: 'break-all',
          }}
        >
          {revealed}
        </div>
        <button type="button" onClick={() => setRevealed(null)} style={{ ...primaryButtonStyle, marginTop: '1rem' }}>
          Done
        </button>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <h2 style={{ color: 'var(--a-ink)', marginTop: 0, fontFamily: 'var(--a-fd-font)' }}>Developer key</h2>
      {error ? <p style={{ color: 'var(--a-hot)', fontSize: '0.84rem' }}>{error}</p> : null}
      {summary.devKey.status === 'not_eligible' ? (
        <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.86rem' }}>
          You are not currently able to self-serve a developer key. Ask your admin if you believe this is wrong.
        </p>
      ) : summary.devKey.status === 'eligible' ? (
        <>
          <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.86rem' }}>
            You can mint a Claude Code developer key: $50 budget over 30 days, AU-resident models only. It is shown to you once.
          </p>
          <button type="button" disabled={busy} onClick={() => void mint()} style={primaryButtonStyle}>
            Mint my developer key
          </button>
        </>
      ) : (
        <>
          {(() => {
            const active = detail && detail.status === 'active' ? detail : null
            const models = active?.models.length ? active.models : undefined
            return (
              <>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <Tile label="Alias" value={summary.devKey.alias} noTruncate minWidth="16rem" />
                  <Tile label="Budget this cycle" value={devKeyCycleLine(summary.devKey.spend, summary.devKey.budget)} />
                  {active ? (
                    <Tile
                      label="Requests / tokens this cycle"
                      value={active.usage.requestsThisCycle.toLocaleString()}
                      sub={`${active.usage.tokensThisCycle.toLocaleString()} tokens`}
                    />
                  ) : null}
                  {active ? <Tile label="Created" value={fmtDate(active.createdAt)} /> : null}
                  {active ? (
                    <Tile
                      label="Budget resets"
                      value={fmtDate(active.budgetResetAt)}
                      sub={active.budgetDuration ? `every ${active.budgetDuration}` : undefined}
                    />
                  ) : null}
                  {active ? <Tile label="Last used" value={fmtDate(active.usage.lastUsed)} /> : null}
                </div>

                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {confirmAction === 'rotate' ? (
                    <>
                      <span style={{ color: 'var(--a-ink-dim)', fontSize: '0.84rem' }}>
                        Rotate your key? The current key stops working immediately and the new one is shown once.
                      </span>
                      <button type="button" disabled={busy} onClick={() => void rotate()} style={primaryButtonStyle}>
                        Rotate
                      </button>
                      <button type="button" disabled={busy} onClick={() => setConfirmAction(null)} style={ghostButtonStyle}>
                        Cancel
                      </button>
                    </>
                  ) : confirmAction === 'revoke' ? (
                    <>
                      <span style={{ color: 'var(--a-hot)', fontSize: '0.84rem' }}>
                        Revoke your key? The current key stops working immediately and is not replaced.
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void revoke()}
                        style={{ padding: '0.5rem 0.95rem', borderRadius: '999px', border: 'none', background: 'var(--a-hot)', color: '#FFFFFF', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--a-fb-font)' }}
                      >
                        Revoke
                      </button>
                      <button type="button" disabled={busy} onClick={() => setConfirmAction(null)} style={ghostButtonStyle}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" disabled={busy} onClick={() => setConfirmAction('rotate')} style={ghostButtonStyle}>
                        Rotate
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setConfirmAction('revoke')}
                        style={{ padding: '0.5rem 0.95rem', borderRadius: '999px', border: 'none', background: 'var(--a-hot)', color: '#FFFFFF', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--a-fb-font)' }}
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </div>
                <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.8rem', marginTop: '0.8rem' }}>Up to 3 rotations per 24 hours.</p>

                {active && active.usage.daily.length > 0 ? (
                  <Panel title="Spend this cycle">
                    <LineChart
                      series={[
                        {
                          name: 'This key',
                          values: active.usage.daily.map((d) => (hideCosts ? d.tokens : (d.spend ?? 0))),
                          color: theme.chartColors[1] ?? theme.accent,
                        },
                      ]}
                      xLabels={active.usage.daily.map((d) => d.day.slice(5))}
                      format={hideCosts ? fmtInt : fmtMoney}
                      label="Spend this cycle"
                    />
                  </Panel>
                ) : null}

                <Panel title="Allowed models">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {(models ?? []).map((m) => (
                      <span
                        key={m}
                        style={{
                          fontSize: '0.78rem',
                          padding: '0.3rem 0.6rem',
                          borderRadius: '999px',
                          background: 'var(--a-hover)',
                          color: 'var(--a-ink)',
                          fontFamily: 'ui-monospace, monospace',
                        }}
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </Panel>

                <Panel title="Using it from Claude Code">
                  <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.84rem', margin: '0 0 0.3rem' }}>
                    Set your key as <code>ANTHROPIC_API_KEY</code>, then:
                  </p>
                  <CodeBlock lines={CLAUDE_CODE_EXPORT_LINES} />
                </Panel>

                <Panel title="Using it from Claude Desktop">
                  <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.84rem', margin: '0 0 0.6rem' }}>
                    This same key works with Claude Desktop in third-party inference mode (Chat, Cowork and Code
                    tabs) -- it calls the same gateway on the same <code>claude-code:*</code> models, so that usage
                    shows up in the totals above rather than as a separate line. Run{' '}
                    <code>scripts/claude-desktop-setup.sh</code> from the platform repo on your Mac; it stores this
                    key in your login Keychain and builds the app's configuration file for you to import. Full steps:
                    see <code>docs/claude-desktop-deployment-guide.md</code>.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', fontSize: '0.82rem' }}>
                      <span style={{ color: 'var(--a-ink-dim)' }}>Gateway URL</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <code style={{ fontFamily: 'ui-monospace, monospace' }}>https://api.chugpt.apps.chu.com.au</code>
                        <CopyButton text="https://api.chugpt.apps.chu.com.au" />
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', fontSize: '0.82rem' }}>
                      <span style={{ color: 'var(--a-ink-dim)' }}>Models</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <code style={{ fontFamily: 'ui-monospace, monospace' }}>{CLAUDE_DESKTOP_MODEL_ALIASES.join(', ')}</code>
                        <CopyButton text={CLAUDE_DESKTOP_MODEL_ALIASES.join(', ')} />
                      </span>
                    </div>
                  </div>
                </Panel>
              </>
            )
          })()}
        </>
      )}
    </div>
  )
}

/**
 * The signed-out landing on the arcade host, restyled onto the same theme
 * system as the rest of the portal (23 Sep 2026) rather than the admin
 * console's cream `SignInScreen` -- a staff member arriving from the
 * onboarding email should land on something that already looks like the
 * board they are about to use, not the operations console.
 */
export function PortalSignInScreen({ onSignIn }: { onSignIn: () => void }) {
  const theme = resolveArcadeTheme('bento')
  return (
    <div className="arcade" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <style>{arcadeCss(theme)}</style>
      <div
        style={{
          width: '100%',
          maxWidth: '24rem',
          background: 'var(--a-panel)',
          border: '1px solid var(--a-line)',
          borderRadius: 'var(--a-radius)',
          boxShadow: 'var(--a-shadow)',
          padding: '2.4rem 2rem',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '3.2rem',
            height: '3.2rem',
            borderRadius: '9999px',
            background: 'var(--a-accent)',
            color: 'var(--a-on-accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '1.3rem',
            margin: '0 auto 1rem',
          }}
        >
          C
        </div>
        <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.3rem', color: 'var(--a-ink)', fontFamily: 'var(--a-fd-font)' }}>CHU-GPT</h1>
        <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.88rem', margin: '0 0 1.6rem' }}>
          Your usage, your rank, and your developer key. Sign in with your CHU Microsoft account.
        </p>
        <button
          type="button"
          onClick={onSignIn}
          style={{ width: '100%', padding: '0.8rem 1.4rem', borderRadius: '999px', border: 'none', background: 'var(--a-accent)', color: 'var(--a-on-accent)', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'var(--a-fb-font)' }}
        >
          Sign in with Microsoft
        </button>
      </div>
    </div>
  )
}

/** `GET /api/me/policy` (`backend/src/routes/policy.ts`). */
interface PolicyStatus {
  currentVersion: string
  accepted: boolean
  acceptedVersion: string | null
  acceptedAt: string | null
  /** The kill switch's live state -- env vars are backend-only, so this
   *  field is the ONLY way the frontend learns whether the gate is
   *  actually enforced right now. */
  enforced: boolean
}

/**
 * The full-screen policy acceptance gate (Round 14, Unit 10,
 * `docs/design/specs/2026-09-24-policy-acceptance-and-api-users.md`). Reuses
 * the SAME staged guide content the Guides tab renders (`findGuide('policy')`)
 * for the policy text itself, so there is exactly one copy of the wording on
 * this platform, never a second one pasted in here to drift from it.
 */
function PolicyGateScreen({
  onAccepted,
  onCancel,
}: {
  onAccepted: () => void
  /** Round 15, Unit 13(b): the standing Home panel opens this SAME screen
   *  as an optional, dismissible modal, since accepting early (before the
   *  gate is enforced) is voluntary. Omitted entirely by `ArcadePortal`'s
   *  own mandatory use -- there is deliberately no way to cancel out of
   *  the real gate, only out of an early, optional read. */
  onCancel?: () => void
}) {
  const theme = resolveArcadeTheme('bento')
  const [checked, setChecked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const guide = findGuide('policy')

  async function accept() {
    setBusy(true)
    setError(null)
    try {
      const result = await apiJson<{ accessGranted: boolean }>('/api/me/policy/accept', { method: 'POST' })
      if (!result.accessGranted) {
        // The acceptance itself IS recorded (the backend's own append-only
        // fact) -- what failed is the Graph write that is supposed to put
        // this person back in CHU-GPT-Users, which is what the rest of the
        // platform (chat, Teams, the API) actually checks. Saying so here
        // rather than closing the gate on a false "all done" that would
        // leave them still locked out everywhere else with no reason why.
        setError(
          'Your acceptance is recorded, but granting access failed. Try again in a moment, or contact the platform team if this continues.',
        )
        return
      }
      onAccepted()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record your acceptance. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="arcade" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <style>{arcadeCss(theme)}</style>
      {/* The embedded preview below shares the SAME .guide-article class the
          Guides tab uses for the real reader, and the same CSS -- see
          guides/articleStyles.ts's own header for why this render needed its
          own copy rather than relying on GuidesPage having mounted first
          (it never does, on this path). */}
      <style>{GUIDE_ARTICLE_CSS}</style>
      <div
        style={{
          width: '100%',
          maxWidth: '34rem',
          background: 'var(--a-panel)',
          border: '1px solid var(--a-line)',
          borderRadius: 'var(--a-radius)',
          boxShadow: 'var(--a-shadow)',
          padding: '2.4rem 2.2rem',
        }}
      >
        <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.4rem', color: 'var(--a-ink)', fontFamily: 'var(--a-fd-font)' }}>
          Before you continue
        </h1>
        <p style={{ color: 'var(--a-ink-dim)', fontSize: '0.92rem', lineHeight: 1.55, margin: '0 0 1.1rem' }}>
          CHU's AI usage policy applies to everything on this platform: chat, Claude Code,
          Teams, and every tool CHU-GPT can reach. Read it once, then carry on.
        </p>

        <div
          style={{
            background: 'var(--a-panel2)',
            border: '1px solid var(--a-line2)',
            borderRadius: '10px',
            padding: '1.1rem 1.2rem',
            maxHeight: '13rem',
            overflowY: 'auto',
            fontSize: '0.86rem',
            color: 'var(--a-ink)',
            lineHeight: 1.6,
            marginBottom: '1.1rem',
          }}
        >
          {guide ? (
            <div className="guide-article" dangerouslySetInnerHTML={{ __html: guide.html }} />
          ) : (
            <p style={{ color: 'var(--a-ink-dim)' }}>
              The policy guide could not be loaded. Refresh and try again; if this
              persists, contact the platform team.
            </p>
          )}
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.88rem', color: 'var(--a-ink)', marginBottom: '1.3rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ marginTop: '0.2rem', accentColor: 'var(--a-accent)' }}
          />
          <span>I have read this policy and agree to follow it.</span>
        </label>

        {error ? <p style={{ color: 'var(--a-hot)', fontSize: '0.84rem', margin: '0 0 0.9rem' }}>{error}</p> : null}

        <button
          type="button"
          disabled={!checked || busy}
          onClick={() => void accept()}
          style={{
            width: '100%',
            padding: '0.85rem 1.4rem',
            borderRadius: '999px',
            border: 'none',
            background: 'var(--a-accent)',
            opacity: !checked || busy ? 0.5 : 1,
            color: 'var(--a-on-accent)',
            fontWeight: 700,
            fontSize: '0.95rem',
            cursor: !checked || busy ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--a-fb-font)',
          }}
        >
          {busy ? 'Recording…' : 'I have read and agree'}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              display: 'block',
              width: '100%',
              marginTop: '0.6rem',
              padding: '0.6rem 1.4rem',
              borderRadius: '999px',
              border: 'none',
              background: 'transparent',
              color: 'var(--a-ink-dim)',
              fontWeight: 600,
              fontSize: '0.86rem',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--a-fb-font)',
            }}
          >
            Not now
          </button>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Wraps `StaffPortalShell` with the policy check every `requireUser` route
 * on this host must pass -- ONE insertion point, so Home, Leaderboard,
 * Developer key and Guides are all covered without a per-tab check that a
 * new tab could forget to add.
 *
 * FAIL CLOSED on a read failure, the same rule every other enforcement
 * point in this system follows (`docs/design/specs/2026-09-24-policy-
 * acceptance-and-api-users.md`'s stated posture): a `GET /api/me/policy`
 * that errors is treated as NOT accepted, never as "the check could not
 * run, so let them through." There is no arcade to point the person at
 * here, because they are already on it -- the error screen offers a retry
 * instead of a redirect.
 */
export function ArcadePortal({
  account,
  isAdmin,
  onSignOut,
}: {
  account: AccountInfo | undefined
  isAdmin: boolean
  onSignOut: () => void
}) {
  const [policy, setPolicy] = useState<PolicyStatus | 'loading' | 'error'>('loading')

  const load = useCallback(async () => {
    setPolicy('loading')
    try {
      setPolicy(await apiJson<PolicyStatus>('/api/me/policy'))
    } catch {
      setPolicy('error')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (policy === 'loading') {
    return (
      <div className="arcade" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{arcadeCss(resolveArcadeTheme('bento'))}</style>
        <p style={{ color: 'var(--a-ink-dim)', fontFamily: 'var(--a-fb-font)' }}>Loading your portal…</p>
      </div>
    )
  }

  if (policy === 'error') {
    return (
      <div className="arcade" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
        <style>{arcadeCss(resolveArcadeTheme('bento'))}</style>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--a-ink)', fontFamily: 'var(--a-fb-font)', marginBottom: '1rem' }}>
            Could not load your account status. Try again.
          </p>
          <button type="button" onClick={() => void load()} style={{ padding: '0.6rem 1.2rem', borderRadius: '999px', border: 'none', background: 'var(--a-accent)', color: 'var(--a-on-accent)', fontWeight: 700, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (policy.enforced && !policy.accepted) {
    return (
      <PolicyGateScreen
        onAccepted={() => setPolicy((p) => (typeof p === 'object' ? { ...p, accepted: true } : p))}
      />
    )
  }

  return <StaffPortalShell account={account} isAdmin={isAdmin} onSignOut={onSignOut} />
}

/**
 * Root of the arcade-host portal. `account` feeds the header's
 * `OperatorAvatar`/display name; `isAdmin`/`onSignOut` are threaded straight
 * through to the reused `LeaderboardPage`, unchanged from before this
 * restyle.
 */
export function StaffPortalShell({
  account,
  isAdmin,
  onSignOut,
}: {
  account: AccountInfo | undefined
  isAdmin: boolean
  onSignOut: () => void
}) {
  const [tab, setTabState] = useState<PortalTab>(() => tabFromPath(window.location.pathname))
  const [summary, setSummary] = useState<MeSummary | null>(null)
  const [activity, setActivity] = useState<MeActivity | null>(null)
  const [devKeyDetail, setDevKeyDetail] = useState<DevKeyDetail | null>(null)
  const [budgetRequests, setBudgetRequests] = useState<BudgetRequest[] | null>(null)
  const [budgetCeilings, setBudgetCeilings] = useState<BudgetCeilings | null>(null)
  const [board, setBoard] = useState<BoardModel | null>(null)
  const [windowSelection, setWindowSelectionState] = useState<ActivitySelection>(() => initialActivitySelection())
  const theme = resolveArcadeTheme('bento')
  // The admin console's "Chubacca's sound" switch (25 Sep 2026). Assumed on
  // until read, as he always starts muted anyway; a failed read keeps it on.
  const [mascotSound, setMascotSound] = useState(true)
  useEffect(() => {
    apiJson<{ sound: boolean }>('/api/mascot')
      .then((r) => setMascotSound(r.sound !== false))
      .catch(() => {})
  }, [])

  /**
   * Round 15, Unit 13(b): a standing panel on Home, INDEPENDENT of the
   * policy gate switch -- this is loaded and shown regardless of
   * `enforced`, so a person can read and accept the policy early, and
   * Maroun can see the whole flow today without needing the switch on.
   * `ArcadePortal`'s own load of this same route (when the gate IS
   * enforced) is a completely separate fetch on a completely separate
   * component; the two never share state, because one blocks the whole
   * portal and the other is an optional panel on one tab of it.
   */
  const [policy, setPolicy] = useState<PolicyStatus | null>(null)
  const loadPolicy = useCallback(async () => {
    try {
      setPolicy(await apiJson<PolicyStatus>('/api/me/policy'))
    } catch {
      // Degrades to "not shown yet" rather than breaking Home -- the same
      // rule loadSummary/loadActivity/loadBoard already follow.
      setPolicy(null)
    }
  }, [])

  function setTab(next: PortalTab) {
    setTabState(next)
    // Same convention as Access.tsx's `?person=` handling: replaceState for
    // a same-page tab switch (not a navigation, must not stack a back-button
    // entry per click), pushState is used nowhere here because these three
    // tabs are one continuous session, not a sequence of pages.
    window.history.replaceState(null, '', pathForTab(next))
  }

  /** Updates the window selection, the URL (so it survives reload and can
   *  be shared), and localStorage (a convenience default for next time) --
   *  the URL is rewritten in full, including the current tab's path, so
   *  this never clobbers `setTab`'s own history entry or vice versa. */
  function setWindowSelection(next: ActivitySelection) {
    setWindowSelectionState(next)
    try {
      window.localStorage.setItem(ACTIVITY_WINDOW_STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Storage can be unavailable (private browsing, quota) -- the
      // selection still works for this session, it just will not be
      // remembered next time.
    }
    const qs = activityQueryString(next).slice(1) // drop the leading '?'
    window.history.replaceState(null, '', `${pathForTab(tab)}?${qs}`)
  }

  async function loadSummary() {
    try {
      const result = await apiJson<MeSummary>('/api/me/summary')
      setSummary(result)
    } catch {
      // A failed summary read degrades to "loading" rather than crashing the
      // portal; the Leaderboard tab and sign-out remain usable regardless.
      setSummary(null)
    }
  }

  async function loadActivity(sel: ActivitySelection) {
    try {
      const result = await apiJson<MeActivity>(`/api/me/activity${activityQueryString(sel)}`)
      setActivity(result)
    } catch {
      // Same rule as loadSummary: a failed read degrades to "no activity
      // tiles yet" rather than breaking the rest of Home.
      setActivity(null)
    }
  }

  async function loadDevKeyDetail() {
    try {
      const result = await apiJson<DevKeyDetail>('/api/me/dev-key/detail')
      setDevKeyDetail(result)
    } catch {
      // Same degrade-not-crash rule: the Developer Key tab still shows the
      // light summary tiles even if the richer detail read fails.
      setDevKeyDetail(null)
    }
  }

  async function loadBudgetRequests() {
    try {
      const result = await apiJson<{ requests: BudgetRequest[]; chat: CeilingInfo; devKey: CeilingInfo | null }>(
        '/api/me/budget-requests',
      )
      setBudgetRequests(result.requests)
      setBudgetCeilings({ chat: result.chat, devKey: result.devKey })
    } catch {
      setBudgetRequests(null)
      setBudgetCeilings(null)
    }
  }

  /**
   * The SAME `/api/leaderboard` the Leaderboard tab reads, in TOKENS mode --
   * never a second ranking endpoint or a spend figure on Home. `toViewModel`
   * is the exact function that page uses to turn the raw payload into a
   * `BoardModel`, so "your team" and the company board below are read off
   * the identical ranked list the board itself shows, not a re-derivation
   * that could disagree with it.
   */
  async function loadBoard(sel: ActivitySelection) {
    const boardWindow = boardWindowKeyFor(sel)
    try {
      const payload = await apiJson<LeaderboardPayload>(`/api/leaderboard?window=${boardWindow}`)
      setBoard(toViewModel(payload, 'tokens', boardWindow))
    } catch {
      // Degrades to no team/company panels rather than breaking Home; the
      // rest of the tab (charts, taxonomy, MCP usage) does not depend on it.
      setBoard(null)
    }
  }

  useEffect(() => {
    void loadSummary()
    void loadPolicy()
  }, [loadPolicy])

  // Refetches whenever the window selection changes (including the initial
  // mount) -- `windowSelection` is part of the dependency array on purpose,
  // so switching the picker is the ONE place these two calls need to fire
  // again, not a second effect to keep in sync with this one.
  useEffect(() => {
    void loadActivity(windowSelection)
    void loadBoard(windowSelection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSelection.key, windowSelection.from, windowSelection.to])

  useEffect(() => {
    // Only paid for when the tab that needs it is actually open, same reason
    // the backend keeps this a sibling read rather than folding it into
    // `/api/me/summary` -- see `routes/me.ts`'s own doc comment on the route.
    // Budget requests are needed by BOTH tabs now (Round 16, Unit 18 split
    // the request form and history off Developer key onto their own tab);
    // the richer key detail (models, usage, the daily chart) stays
    // Developer-key-only, since Budget only ever reads the light ceiling
    // already on `summary.devKey`.
    if (tab === 'devkey') {
      void loadDevKeyDetail()
      void loadBudgetRequests()
    } else if (tab === 'budget') {
      void loadBudgetRequests()
    }
  }, [tab])

  if (tab === 'leaderboard') {
    // The existing board owns its own full-screen chrome (including its own
    // sign-out and admin-console link), so it is NOT wrapped in the portal
    // TabBar above -- rendering both would duplicate that chrome. Returning
    // to Home/Developer key happens via the browser; restyling
    // LeaderboardPage's own header to add a "back to portal" control is a
    // reasonable follow-up, not done here.
    return <LeaderboardPage isAdmin={isAdmin} onSignOut={onSignOut} />
  }

  return (
    <div className="arcade" style={{ minHeight: '100vh', padding: '1.5rem 1rem' }}>
      <style>{arcadeCss(theme)}</style>
      {/* The header row: title on the left, Chubacca on the right, sitting
          directly above the nav pill's Sign out (Maroun, 24 Sep 2026). His
          PNG carries its own alpha, so there is deliberately no panel, fill
          or border behind him. Aligned to the bottom so he stands on the
          nav bar rather than floating. No "CHU-GPT" eyebrow any more. */}
      <div
        data-portal-header=""
        style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', margin: '0 0 0.4rem' }}
      >
        <ArcadeHeader theme={theme} title="CHU AI Platform" subtitle="YOUR OWN USAGE, RANK AND DEVELOPER KEY" />
        {/* He rests ON the nav bar: the mascot seats each clip's paw line
            at its own bottom edge, the negative margin cancels this row's
            0.4rem bottom margin and adds 0.2rem so his paws overlap the
            bar's top edge rather than touching it (v0.58.0 hovered), and
            zIndex keeps them over the bar's shadow. */}
        <ChubaccaMascot
          soundAllowed={mascotSound}
          style={{
            display: 'block',
            height: '8.5rem',
            width: 'auto',
            marginRight: '1.5rem',
            marginBottom: '-0.6rem',
            position: 'relative',
            zIndex: 1,
            flexShrink: 0,
          }}
        />
      </div>
      <TabBar tab={tab} setTab={setTab} account={account} onSignOut={onSignOut} isAdmin={isAdmin} />
      {tab === 'home' ? (
        <HomeTab
          summary={summary}
          activity={activity}
          board={board}
          theme={theme}
          windowSelection={windowSelection}
          onWindowChange={setWindowSelection}
          onOpenDevKey={() => setTab('devkey')}
          onOpenPolicy={() => {
            setTab('guides')
            // After setTab's own replaceState: GuidesPage reads its slug from
            // the path when it mounts on the next render.
            window.history.replaceState(null, '', '/guides/policy')
          }}
          policy={policy}
          onPolicyAccepted={loadPolicy}
        />
      ) : null}
      {tab === 'devkey' ? (
        <DeveloperKeyTab
          summary={summary}
          detail={devKeyDetail}
          hideCosts={summary?.hideCosts ?? false}
          theme={theme}
          onChanged={() => {
            void loadSummary()
            void loadDevKeyDetail()
          }}
        />
      ) : null}
      {tab === 'budget' ? (
        <BudgetTab
          summary={summary}
          budgetRequests={budgetRequests}
          ceilings={budgetCeilings}
          onBudgetRequestsChanged={() => void loadBudgetRequests()}
        />
      ) : null}
      {tab === 'guides' ? <GuidesPage chrome="embedded" /> : null}
    </div>
  )
}
